// client/src/admin/instagram/FollowClaims.js
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Button,
  Avatar,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  HowToReg as ClaimsIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Refresh as RefreshIcon,
  Instagram as InstagramIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const STATUS_COLORS = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

const FollowClaims = ({ lang: propLang }) => {
  const theme = useTheme();
  const { hitAxios } = useContext(GlobalContext);
  const { lang: ctxLang } = useContext(TranslateContext);
  const t = (k) => propLang?.[k] || ctxLang?.[k] || k;

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClaims();
  }, []);

  async function fetchClaims() {
    setLoading(true);
    const res = await hitAxios({
      path: "/api/instagram/admin/claims",
      admin: true,
      post: false,
      showLoading: false,
    });
    setLoading(false);
    if (res?.data?.success) setClaims(res.data.data || []);
  }

  async function handleApprove(claim) {
    const res = await hitAxios({
      path: "/api/instagram/admin/claims/approve",
      admin: true,
      post: true,
      obj: { id: claim.id, uid: claim.uid },
    });
    if (res?.data?.success) fetchClaims();
  }

  async function handleReject(claim) {
    const res = await hitAxios({
      path: "/api/instagram/admin/claims/reject",
      admin: true,
      post: true,
      obj: { id: claim.id },
    });
    if (res?.data?.success) fetchClaims();
  }

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  return (
    <Box>
      <PageHeader
        title={t("followClaims") || "Follow Claims"}
        subtitle="Review and approve users who followed your Instagram to earn daily tokens"
        icon={ClaimsIcon}
        primaryAction={
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={fetchClaims}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        }
      />

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  "User",
                  "Instagram Username",
                  "Status",
                  "Daily Limit",
                  "Submitted",
                  "Actions",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 700, fontSize: "0.75rem", py: 1.5 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {claims.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 5, color: "text.disabled" }}
                  >
                    {loading ? "Loading..." : "No claims yet"}
                  </TableCell>
                </TableRow>
              ) : (
                claims.map((claim) => (
                  <TableRow key={claim.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: 11,
                            bgcolor: "primary.main",
                          }}
                        >
                          {claim.user_name?.[0]?.toUpperCase() || "U"}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            display="block"
                            noWrap
                            sx={{ maxWidth: 120 }}
                          >
                            {claim.user_name || "—"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            noWrap
                            sx={{ maxWidth: 120, display: "block" }}
                          >
                            {claim.user_email || "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <InstagramIcon
                          sx={{ fontSize: 14, color: "#E1306C" }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="text.primary"
                        >
                          @{claim.instagram_username}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={claim.status}
                        size="small"
                        color={STATUS_COLORS[claim.status] || "default"}
                        sx={{ fontSize: "0.65rem", height: 20 }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {claim.daily_token_limit > 0
                          ? `${claim.daily_token_limit} tokens/day`
                          : "Not set"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(claim.createdAt)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {claim.status === "pending" && (
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Approve — Grant daily tokens">
                            <IconButton
                              size="small"
                              onClick={() => handleApprove(claim)}
                              sx={{
                                color: "success.main",
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.success.main,
                                    0.1,
                                  ),
                                },
                              }}
                            >
                              <ApproveIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              onClick={() => handleReject(claim)}
                              sx={{
                                color: "error.main",
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                },
                              }}
                            >
                              <RejectIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                      {claim.status === "approved" && (
                        <Typography
                          variant="caption"
                          color="success.main"
                          fontWeight={600}
                        >
                          ✓ Approved
                        </Typography>
                      )}
                      {claim.status === "rejected" && (
                        <Typography
                          variant="caption"
                          color="error.main"
                          fontWeight={600}
                        >
                          ✗ Rejected
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default FollowClaims;
